import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, Eye, ArrowUpDown, Loader2, Clock, CheckCircle, Package, Truck, AlertTriangle, ChevronLeft, ChevronRight, Edit, Trash2, FileText, Mail } from 'lucide-react';
import { adminGetAllOrders } from '../../../services/admin/shopAdminService';
import { useAuth } from '../../../hooks/useAuth';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription 
} from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/useToast';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { useDomainQuery } from '../../../hooks/useDomainQuery';

// Interfaces de base
interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  status: string;
  payment_status?: string | null; // Optionnel car peut être absent
  total_amount: number;
  currency?: string; // Optionnel car peut être absent
  created_at: string;
}

// Fonction pour obtenir la couleur du badge en fonction du statut
const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending_payment':
      return 'bg-yellow-500/20 text-yellow-500';
    case 'paid':
      return 'bg-green-500/20 text-green-500';
    case 'processing':
      return 'bg-blue-500/20 text-blue-500';
    case 'shipped':
      return 'bg-purple-500/20 text-purple-500';
    case 'delivered':
      return 'bg-green-500/20 text-green-500';
    case 'cancelled':
      return 'bg-[#D4AF37]/20 text-[#D4AF37]';
    case 'inquiry':
      return 'bg-gray-500/20 text-gray-500';
    default:
      return 'bg-gray-500/20 text-gray-500';
  }
};

// Fonction pour obtenir l'icône du statut
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending_payment':
      return <Clock className="h-4 w-4" />;
    case 'paid':
      return <CheckCircle className="h-4 w-4" />;
    case 'processing':
      return <Package className="h-4 w-4" />;
    case 'shipped':
      return <Truck className="h-4 w-4" />;
    case 'delivered':
      return <CheckCircle className="h-4 w-4" />;
    case 'cancelled':
      return <AlertTriangle className="h-4 w-4" />;
    case 'inquiry':
      return <Package className="h-4 w-4" />;
    default:
      return <Package className="h-4 w-4" />;
  }
};

const getPaymentStatusBadgeVariant = (status: string | null | undefined) => {
  switch (status?.toLowerCase()) {
    case 'paid': return 'success';
    case 'pending': return 'warning';
    case 'failed': return 'destructive';
    default: return 'secondary';
  }
};

const AdminOrderList: React.FC = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { applyDomainFilter } = useDomainQuery();
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/admin/boutique/commandes' } });
    }
  }, [user, navigate]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 AdminOrderList - Chargement des commandes...');
      const ordersData = await adminGetAllOrders();
      console.log('📦 AdminOrderList - Commandes récupérées:', ordersData?.length || 0, ordersData);
      setOrders(ordersData);
    } catch (err) {
      console.error('❌ AdminOrderList - Erreur lors du chargement:', err);
      setError("Erreur lors du chargement des commandes");
      toast({
        title: "Erreur",
        description: "Impossible de charger les commandes. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);
  
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const filteredOrders = orders
    .filter((order: Order) => {
      const searchMatch = searchTerm === '' || 
        order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const statusMatch = statusFilter === 'all' || order.status === statusFilter;
      
      return searchMatch && statusMatch;
    })
    .sort((a: Order, b: Order) => {
      const fieldA = a[sortField as keyof Order];
      const fieldB = b[sortField as keyof Order];

      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      }
      if (typeof fieldA === 'number' && typeof fieldB === 'number') {
        return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
      }
      // Fallback for dates
      return sortDirection === 'asc' 
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-full py-32">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="bg-transparent border-none shadow-none">
          <CardHeader className="px-0 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl md:text-3xl text-white">Commandes</CardTitle>
              <CardDescription className="text-gray-400">Consultez et gérez les commandes de la boutique.</CardDescription>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
                <Button onClick={() => loadOrders()} variant="outline" size="sm" className="ml-auto bg-white/5 border-white/10 hover:bg-white/10 text-white">
                    <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>
          </CardHeader>
          <CardContent className="px-0">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher une commande..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-card bg-white/5 text-white placeholder-gray-400"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-auto bg-card bg-white/5 text-gray-400 border border-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-background"
              >
                <option value="all">Tous les statuts</option>
                <option value="paid">Payée</option>
                <option value="pending_payment">En attente de paiement</option>
                <option value="processing">En traitement</option>
                <option value="shipped">Expédiée</option>
                <option value="delivered">Livrée</option>
                <option value="cancelled">Annulée</option>
                <option value="inquiry">Demande d'info</option>
              </select>
            </div>

            {error && <p className="text-[#D4AF37] text-center py-8">{error}</p>}

            {!loading && !error && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader className="border-b border-white/10">
                    <TableRow>
                      {[{key: 'order_number', label: 'N° Commande'}, {key: 'customer_name', label: 'Client'}, {key: 'created_at', label: 'Date'}, {key: 'total_amount', label: 'Total'}].map(header => (
                        <TableHead key={header.key} className="cursor-pointer text-gray-400 hover:text-white transition-colors" onClick={() => handleSort(header.key)}>
                          <div className="flex items-center">
                            {header.label}
                            {sortField === header.key && <ArrowUpDown className="ml-2 h-4 w-4" />}
                          </div>
                        </TableHead>
                      ))}
                      <TableHead className="text-gray-400 hover:text-white transition-colors">Statut</TableHead>
                      <TableHead className="text-right text-gray-400 hover:text-white transition-colors">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.length > 0 ? filteredOrders
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((order: Order, index: number) => (
                      <TableRow key={order.id} className={`${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                        <TableCell>
  <span 
    className="font-bold text-lg text-[#D4AF37] hover:text-[#D4AF37]/70 hover:underline cursor-pointer transition-colors duration-200" 
    onClick={() => navigate(`/admin/boutique/commande/${order.id}`)}
  >
    {order.order_number}
  </span>
</TableCell>
                        <TableCell>
                          <div className="font-medium text-white">{order.customer_name}</div>
                          <div className="text-sm text-gray-300">{order.customer_email}</div>
                        </TableCell>
                        <TableCell className="text-white">{new Date(order.created_at).toLocaleDateString('fr-FR')}<br /><span className="text-xs text-gray-300">{new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span></TableCell>
                        <TableCell className="text-white">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: order.currency === 'FCFA' ? 'XAF' : (order.currency || 'EUR') }).format(order.total_amount)}</TableCell>
                        <TableCell className="text-white">
  <div className={`inline-flex items-center px-3 py-1 rounded-full gap-1 ${getStatusColor(order.status)}`}>
    {getStatusIcon(order.status)}
    <span>{order.status}</span>
  </div>
</TableCell>
                        <TableCell className="text-right text-white">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="text-white hover:text-white/80" onClick={() => navigate(`/admin/boutique/commande/${order.id}`)} title="Voir les détails">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-blue-500 hover:text-blue-400" onClick={() => navigate(`/admin/boutique/commande/${order.id}`)} title="Modifier la commande">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-green-500 hover:text-green-400" onClick={() => window.open(`/facture/${order.id}`, '_blank')} title="Voir la facture">
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-purple-500 hover:text-purple-400" onClick={() => window.open(`mailto:${order.customer_email}?subject=Votre commande ${order.order_number}`, '_blank')} title="Envoyer un email">
                              <Mail className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center text-gray-400">Aucune commande trouvée.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {filteredOrders.length > itemsPerPage && (
                  <div className="flex justify-between items-center p-4 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                      Affichage de {Math.min((currentPage - 1) * itemsPerPage + 1, filteredOrders.length)} à {Math.min(currentPage * itemsPerPage, filteredOrders.length)} sur {filteredOrders.length} commandes
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-white">{currentPage} / {Math.ceil(filteredOrders.length / itemsPerPage)}</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredOrders.length / itemsPerPage)))}
                        disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                        className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminOrderList;
